import classNames from 'classnames';
import { Tag } from 'components/Tag/Tag';
import { constTrue, identity } from 'fp-ts/lib/function';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ChangeEvent,
  ClipboardEvent,
  FC,
  KeyboardEvent,
  ReactNode,
  SyntheticEvent,
} from 'react';
import './MultiTextInput.css';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { copyToClipboard } from '@ipfabric/ui-components/utils/copyToClipboard';
import type { DropdownMenuItem } from 'components/DropdownMenu/DropdownMenu';
import { OverflowMenu } from 'components/DropdownMenu/OverflowMenu';
import { OverflowMenuButtonLegacy } from 'components/DropdownMenu/OverflowMenuButton';
import { FormInputButton } from 'components/FormInputButton.tsx/FormInputButton';
import { array } from 'fp-ts';
import {
  type AllowedCharactersPattern,
  makeWithAllowedCharacters,
} from '../../utils/regexp/withAllowedCharacters';

export interface MultiTextInputProps {
  allowedCharactersPattern?: AllowedCharactersPattern;
  autoFocus?: boolean;
  className?: string;
  hasError?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  value: string[];
  validateItem?: (value: string) => boolean;
  contextMenuItems?: DropdownMenuItem[];
  inputRef?: RefObject<HTMLInputElement>;
  usage?: 'normal' | 'cidr';
  /*
   * Values to be hidden in input. Usable for filtering values without
   * adding complex logic handling changed indexes and mapping non present
   * values
   */
  hiddenValues?: string[];
  infoMessage?: ReactNode;
  /**
   * Container for context menu positioning
   */
  container?: HTMLElement | null;
}

const multiTextInputMsg = {
  clear: 'Clear all',
  copyToClipboard: 'Copy to clipboard',
  copied: 'Copied',
};

export const MultiTextInput: FC<MultiTextInputProps> = ({
  validateItem = constTrue,
  hiddenValues = [],
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const usedInputRef = props.inputRef ?? inputRef;
  const spanRef = useRef<HTMLSpanElement>(null);
  const [placeholderIsShown, setPlaceholderIsShown] = useState<boolean>(true);
  /**
   * The timer creates a slight delay before the placeholder appears,
   * allowing the user to click on the buttons inside the input.
   */
  const blurTimerRef = useRef<number | null>(null);
  const BLUR_SWITCHING_TIME = 100;
  const cleanInputValue = props.allowedCharactersPattern
    ? makeWithAllowedCharacters(props.allowedCharactersPattern)
    : identity;

  const addValue = (...values: string[]) => {
    const cleanedValues = values
      .map((val) => val.trim())
      .map((val) => val.replace(/[\n\t]/g, ' '))
      .map(cleanInputValue)
      .filter((val) => val.length);

    const uniqueValues = new Set([...props.value, ...cleanedValues]);

    props.onChange([...uniqueValues]);
  };

  const getStartEndOfSelection = (element: HTMLInputElement) => {
    const caretStart = element.selectionStart ?? element.value.length; // fallback to end of input
    const caretEnd = element.selectionEnd ?? element.value.length; // fallback to end of input
    return [caretStart, caretEnd];
  };

  const handleComponentClick = () => {
    usedInputRef.current?.focus();
  };

  const handleRemove = (index: number) => {
    props.onChange(array.unsafeDeleteAt(index, props.value));
    usedInputRef.current?.focus();
  };

  const handleClear = useStableCallback(() => {
    props.onChange([]);
    usedInputRef.current?.focus();
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPlaceholderIsShown(false);
    setInputValue(cleanInputValue(e.target.value));
  };

  const handleInputBlur = () => {
    props.onBlur?.();

    if (inputValue !== '') {
      addValue(inputValue);

      setInputValue('');
    }

    const timer = window.setTimeout(() => {
      setPlaceholderIsShown(true);
    }, BLUR_SWITCHING_TIME);

    blurTimerRef.current = timer;
  };

  const handleInputFocus = () => {
    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }

    setPlaceholderIsShown(false);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setInputValue('');
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue !== '') {
        addValue(inputValue);
        setInputValue('');
      }
    }

    if (e.key === ',') {
      e.preventDefault();
      const {
        currentTarget: { value },
      } = e;
      const [caretStart, caretEnd] = getStartEndOfSelection(e.currentTarget);
      const isCaretAtEnd =
        caretStart === value.length && caretEnd === value.length;

      if (isCaretAtEnd) {
        addValue(inputValue);
        setInputValue('');
      } else {
        const textBeforeCaret = value.slice(0, caretStart);
        const textAfterCaret = value.slice(caretEnd, value.length);
        addValue(textBeforeCaret);
        setInputValue(textAfterCaret);
      }
    }
  };

  const handleCopyToClipboardClick = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setCopied(true);
    copyToClipboard(props.value.join(','));
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value },
    } = event;
    const [caretStart, caretEnd] = getStartEndOfSelection(event.currentTarget);
    const clipboardText = event.clipboardData.getData('text');
    const mergedText = `${value.slice(0, caretStart)}${clipboardText}${value.slice(caretEnd, value.length)}`;

    if (!mergedText.includes(',')) {
      setInputValue(mergedText);
    } else {
      const values = mergedText.split(',');
      addValue(...values);
      setInputValue('');
    }

    event.preventDefault();
  };

  const contextMenuItems = useMemo(
    () => [
      {
        label:
          props.value.length === 0
            ? multiTextInputMsg.clear
            : `${multiTextInputMsg.clear} (${props.value.length})`,
        disabled: props.value.length === 0,
        onClick: handleClear,
      },
      ...(props.contextMenuItems ? props.contextMenuItems : []),
    ],
    [props.value, props.contextMenuItems],
  );

  const measureTextWidth = useStableCallback((text: string): number => {
    if (spanRef.current) {
      spanRef.current.textContent = text;
      return spanRef.current.offsetWidth;
    }

    return 0;
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies(usedInputRef.current): ref
  // biome-ignore lint/correctness/useExhaustiveDependencies(usedInputRef.current.style): ref
  useEffect(() => {
    if (usedInputRef.current) {
      usedInputRef.current.style.width = `${
        placeholderIsShown
          ? measureTextWidth(props.placeholder || '')
          : measureTextWidth(inputValue)
      }px`;
    }
  }, [inputValue, props.placeholder, placeholderIsShown]);

  useEffect(
    () => () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
    },
    [],
  );

  return (
    <div
      className={classNames(
        'multi-text-input',
        props.usage === 'cidr' && 'multi-text-input--cidr',
        props.disabled && 'multi-text-input--disabled',
        props.hasError && 'multi-text-input--error',
        props.className,
      )}
      onClick={handleComponentClick}
    >
      <ScrollView autoHeight autoHeightMax='10rem'>
        {props.infoMessage && <div>{props.infoMessage}</div>}
        <div className='flex flex-wrap items-center py-0.5'>
          {props.value.map((value, index) =>
            hiddenValues.includes(value) ? null : (
              <Tag
                disabled={props.disabled}
                className='my-1 mr-2'
                key={value}
                onRemove={() => {
                  handleRemove(index);
                }}
                hasError={!validateItem(value)}
              >
                {value.replace(/ {2}/g, ' \u00A0')}
              </Tag>
            ),
          )}
          <div className='flex'>
            <input
              autoFocus={props.autoFocus}
              disabled={props.disabled}
              value={inputValue}
              onChange={handleInputChange}
              onPaste={handlePaste}
              type='text'
              ref={usedInputRef}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholderIsShown ? props.placeholder : ''}
            />
            <span className='multi-text-input__placeholder' ref={spanRef} />
          </div>
        </div>
      </ScrollView>
      <div className='multi-text-input__suffix'>
        <Tooltip
          placement='top'
          content={
            copied
              ? multiTextInputMsg.copied
              : multiTextInputMsg.copyToClipboard
          }
          onHideTooltip={() => {
            setCopied(false);
          }}
        >
          <FormInputButton
            onClick={handleCopyToClipboardClick}
            disabled={props.disabled || props.value.length === 0}
          >
            <Icon size='sm' glyph='fa-copy' iconStyle='regular' />
          </FormInputButton>
        </Tooltip>
        <div className='relative flex items-center'>
          <OverflowMenu
            className='ml-1'
            ButtonComponent={OverflowMenuButtonLegacy}
            defaultMenuPosition={['left', 'down']}
            disabled={props.disabled}
            items={contextMenuItems}
            container={props.container}
          />
        </div>
      </div>
    </div>
  );
};
